import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmDialogContext";
import { isoToUserParts, userPartsToIso, formatInUserTimezone } from "@/lib/timezone";
import RichTextEditor from "@/components/RichTextEditor";
import { ArrowLeft, Sparkles, Send, Save, Wand2, Clock, FileText, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TONES = ["professional", "friendly", "playful", "urgent", "luxurious"];
const LABELS = ["A", "B", "C", "D", "E", "F", "G", "H"];

const MODES = [
  { value: "single", label: "Single — one subject + one body" },
  { value: "test", label: "Multivariate test — pick a winner" },
  { value: "rotate", label: "Rotate — random variant per contact" },
  { value: "segmented", label: "Segmented — variant by contact tag" },
];

const defaultVariant = (label, content = "") => ({
  label, subject: "", content, tag: "",
});

export default function CampaignBuilderPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const userTz = user?.timezone || "UTC";

  const [audiences, setAudiences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [form, setForm] = useState({
    name: "", subject: "", preview_text: "",
    from_name: "", from_email: "", reply_to: "",
    audience_id: "", content: "",
    filter_tag: "", scheduled_at: "",
    mode: "single", test_split_percent: 20,
    variants: [defaultVariant("A")],
  });
  const [activeVariantIdx, setActiveVariantIdx] = useState(0);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  // AI / template dialogs
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMode, setAiMode] = useState("subject");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiTone, setAiTone] = useState("professional");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResults, setAiResults] = useState([]);
  const [aiContent, setAiContent] = useState("");
  const [tplOpen, setTplOpen] = useState(false);
  const [verifiedDomains, setVerifiedDomains] = useState([]);

  useEffect(() => {
    (async () => {
      const [auds, tpls, doms] = await Promise.all([
        api.get("/audiences"),
        api.get("/templates"),
        api.get("/sending-domains").catch(() => ({ data: [] })),
      ]);
      setAudiences(auds.data);
      setTemplates(tpls.data);
      setVerifiedDomains((doms.data || []).filter((d) => d.verified));
      if (isEdit) {
        const { data: c } = await api.get(`/campaigns/${id}`);
        const variants = (c.variants && c.variants.length)
          ? c.variants.map((v) => ({ label: v.label, subject: v.subject, content: v.content, tag: v.tag || "" }))
          : [{ label: "A", subject: c.subject, content: c.content, tag: "" }];
        setForm({
          name: c.name, subject: c.subject, preview_text: c.preview_text,
          from_name: c.from_name, from_email: c.from_email, reply_to: c.reply_to || "",
          audience_id: c.audience_id, content: c.content,
          filter_tag: c.filter_tag || "", scheduled_at: c.scheduled_at || "",
          mode: c.mode || "single", test_split_percent: c.test_split_percent || 20,
          variants,
        });
        setLoading(false);
      } else {
        setForm((f) => ({
          ...f,
          from_name: user?.name || "",
          from_email: user?.email || "",
          audience_id: auds.data[0]?.id || "",
        }));
      }
    })();
  }, [id, isEdit, user]);

  useEffect(() => {
    if (!form.audience_id) { setTags([]); return; }
    api.get(`/audiences/${form.audience_id}/tags`).then((r) => setTags(r.data.tags || []));
  }, [form.audience_id]);

  const buildPayload = () => {
    // Keep variant[0] in sync with legacy subject/content
    const v0 = form.variants[0] || defaultVariant("A");
    const cleanVariants = form.variants.map((v) => ({
      label: v.label, subject: v.subject, content: v.content,
      tag: form.mode === "segmented" ? (v.tag || null) : null,
    }));
    const payload = {
      ...form,
      subject: v0.subject || form.subject,
      content: v0.content || form.content,
      variants: cleanVariants,
    };
    if (!payload.filter_tag) payload.filter_tag = null;
    if (!payload.scheduled_at) payload.scheduled_at = null;
    if (!payload.reply_to) payload.reply_to = null;
    if (payload.mode === "single") payload.variants = [cleanVariants[0]];
    return payload;
  };

  const save = async (silent = false) => {
    if (!form.name || !form.variants[0]?.subject || !form.audience_id) {
      toast.error("Name, subject and audience are required");
      return null;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const data = isEdit
        ? (await api.put(`/campaigns/${id}`, payload)).data
        : (await api.post("/campaigns", payload)).data;
      if (!silent) toast.success("Campaign saved");
      if (!isEdit && !silent) navigate(`/campaigns/${data.id}/edit`, { replace: true });
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    const ok = await confirm({
      title: "Send campaign now?",
      description: "This cannot be undone. Emails will be sent to your audience immediately.",
      confirmText: "Send now",
    });
    if (!ok) return;
    setSending(true);
    try {
      let cid = id;
      if (!isEdit) {
        const saved = await save(true);
        if (!saved) return;
        cid = saved.id;
      } else {
        // Persist latest edits before sending
        const saved = await save(true);
        if (!saved) return;
      }
      const { data } = await api.post(`/campaigns/${cid}/send`);
      const progress = data.send_progress;
      if (data.status === "sending") {
        toast.success(data.message || "Campaign is sending in the background");
        navigate(`/campaigns/${cid}/analytics`);
        return;
      }
      toast.success(`Sent to ${data.recipients ?? progress?.sent ?? 0} recipients`);
      navigate(`/campaigns/${cid}/analytics`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const schedule = async () => {
    if (!form.scheduled_at) return toast.error("Pick a scheduled date/time first");
    const date = new Date(form.scheduled_at);
    if (date <= new Date()) return toast.error("Scheduled time must be in the future");
    const ok = await confirm({
      title: "Schedule campaign?",
      description: `Send this campaign on ${formatInUserTimezone(form.scheduled_at, userTz)} (${userTz})?`,
      confirmText: "Schedule",
    });
    if (!ok) return;
    let cid = id;
    const saved = await save(true);
    if (!saved) return;
    cid = saved.id;
    setScheduling(true);
    try {
      await api.post(`/campaigns/${cid}/schedule`);
      toast.success(`Scheduled for ${formatInUserTimezone(form.scheduled_at, userTz)} (${userTz})`);
      navigate("/campaigns");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Schedule failed");
    } finally {
      setScheduling(false);
    }
  };

  const runAI = async () => {
    if (!aiPrompt.trim()) return toast.error("Describe your email topic");
    setAiLoading(true);
    setAiResults([]);
    setAiContent("");
    try {
      if (aiMode === "subject") {
        const { data } = await api.post("/ai/subject-line", { prompt: aiPrompt, tone: aiTone });
        setAiResults(data.suggestions || []);
      } else {
        const { data } = await api.post("/ai/email-content", { topic: aiPrompt, tone: aiTone });
        setAiContent(data.content || "");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI failed");
    } finally {
      setAiLoading(false);
    }
  };

  const updateVariant = (idx, patch) => {
    const next = [...form.variants];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, variants: next });
  };

  const applyAI = (value) => {
    if (aiMode === "subject") updateVariant(activeVariantIdx, { subject: value });
    else updateVariant(activeVariantIdx, { content: value });
    setAiOpen(false);
    toast.success(`Applied to variant ${form.variants[activeVariantIdx].label}`);
  };

  const applyTemplate = (t) => {
    updateVariant(activeVariantIdx, { content: t.content });
    setForm((f) => ({ ...f, preview_text: t.preview_text || f.preview_text }));
    setTplOpen(false);
    toast.success(`Template "${t.name}" applied to variant ${form.variants[activeVariantIdx].label}`);
  };

  const addVariant = () => {
    if (form.variants.length >= LABELS.length) return toast.error("Maximum variants reached");
    const newLabel = LABELS[form.variants.length];
    setForm({ ...form, variants: [...form.variants, defaultVariant(newLabel)] });
    setActiveVariantIdx(form.variants.length);
  };

  const removeVariant = (idx) => {
    if (form.variants.length <= 1) return;
    const next = form.variants.filter((_, i) => i !== idx);
    // Re-label
    next.forEach((v, i) => { v.label = LABELS[i]; });
    setForm({ ...form, variants: next });
    setActiveVariantIdx(Math.max(0, Math.min(activeVariantIdx, next.length - 1)));
  };

  if (loading) return <div className="p-12 text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>;

  const isMulti = form.mode !== "single";
  const activeVariant = form.variants[activeVariantIdx];

  return (
    <div data-testid="campaign-builder">
      <div className="border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/campaigns" className="inline-flex items-center text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50">
            <ArrowLeft className="h-4 w-4 mr-1" /> Campaigns
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => save()} disabled={saving} className="rounded-sm font-semibold" data-testid="save-campaign-button">
              <Save className="h-4 w-4 mr-2" /> {saving ? "Saving..." : "Save draft"}
            </Button>
            {form.scheduled_at && (
              <Button variant="outline" onClick={schedule} disabled={scheduling} className="rounded-sm font-semibold" data-testid="schedule-campaign-button">
                <Clock className="h-4 w-4 mr-2" /> {scheduling ? "Scheduling..." : "Schedule"}
              </Button>
            )}
            <Button onClick={send} disabled={sending || saving} className="rounded-sm btn-brand font-semibold" data-testid="send-campaign-button">
              <Send className="h-4 w-4 mr-2" /> {sending ? "Starting send..." : "Send now"}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 space-y-8">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div className="space-y-6">
            <div>
              <p className="label-overline mb-2">Step 01</p>
              <h2 className="font-display font-bold text-2xl tracking-tight">Setup</h2>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Campaign name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm" placeholder="Spring Newsletter" data-testid="campaign-name-input" />
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Audience</Label>
              <Select value={form.audience_id} onValueChange={(v) => setForm({ ...form, audience_id: v })}>
                <SelectTrigger className="mt-2 rounded-sm" data-testid="campaign-audience-select"><SelectValue placeholder="Select audience" /></SelectTrigger>
                <SelectContent>
                  {audiences.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.contact_count})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {tags.length > 0 && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Filter by tag</Label>
                <Select value={form.filter_tag || "__all__"} onValueChange={(v) => setForm({ ...form, filter_tag: v === "__all__" ? "" : v })}>
                  <SelectTrigger className="mt-2 rounded-sm" data-testid="campaign-tag-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All contacts</SelectItem>
                    {tags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">From name</Label>
                <Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} className="mt-2 rounded-sm" data-testid="campaign-from-name-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">From email</Label>
                <Input type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} className="mt-2 rounded-sm" data-testid="campaign-from-email-input" />
                {verifiedDomains.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {verifiedDomains.slice(0, 4).map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => setForm({ ...form, from_email: `noreply@${d.domain}` })}
                        className="text-xs px-2 py-0.5 rounded-sm border border-neutral-200 dark:border-neutral-800 hover:border-neutral-900 text-neutral-600 dark:text-neutral-400"
                        data-testid={`from-domain-${d.id}`}
                      >
                        @{d.domain} ✓
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Reply-to email (optional)</Label>
              <Input type="email" value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} className="mt-2 rounded-sm" placeholder="replies@yourdomain.com" data-testid="campaign-reply-to-input" />
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Where replies land. Leaves From email if blank.</p>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Preview text</Label>
              <Input value={form.preview_text} onChange={(e) => setForm({ ...form, preview_text: e.target.value })} className="mt-2 rounded-sm" placeholder="Shown in inbox preview" data-testid="campaign-preview-input" />
            </div>

            <div className="pt-4 border-t border-neutral-200 dark:border-neutral-800">
              <p className="label-overline mb-2">Step 03</p>
              <h2 className="font-display font-bold text-2xl tracking-tight">Send</h2>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Schedule (optional)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                <Input
                  type="date"
                  value={isoToUserParts(form.scheduled_at, userTz).date}
                  onChange={(e) => setForm({
                    ...form,
                    scheduled_at: userPartsToIso(e.target.value, isoToUserParts(form.scheduled_at, userTz).time, userTz),
                  })}
                  className="rounded-sm"
                  data-testid="campaign-schedule-date"
                />
                <Input
                  type="time"
                  value={isoToUserParts(form.scheduled_at, userTz).time}
                  onChange={(e) => setForm({
                    ...form,
                    scheduled_at: userPartsToIso(isoToUserParts(form.scheduled_at, userTz).date, e.target.value, userTz),
                  })}
                  className="rounded-sm"
                  data-testid="campaign-schedule-time"
                />
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                Times use your account timezone ({userTz}). Change it in{" "}
                <Link to="/settings" className="underline underline-offset-2">Settings</Link>.
                Pick date and time, then click <strong>Schedule</strong> or <strong>Save draft</strong>.
              </p>
            </div>
          </div>

          <div className="lg:sticky lg:top-24 lg:self-start h-full">
            <p className="label-overline mb-3">Live preview {isMulti && `— ${activeVariant.label}`}</p>
            <div className="lift-card bg-white dark:bg-neutral-950 rounded-sm overflow-hidden h-full flex flex-col max-h-[520px]">
              <div className="px-6 py-4 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 shrink-0">
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">From</div>
                <div className="text-sm font-semibold">{form.from_name || "Sender"} <span className="text-neutral-400 font-normal">&lt;{form.from_email || "you@example.com"}&gt;</span></div>
                {form.reply_to && <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Reply-to: {form.reply_to}</div>}
                <div className="mt-3 text-xs text-neutral-500 dark:text-neutral-400 mb-1">Subject</div>
                <div className="font-display font-bold text-lg leading-snug">{activeVariant.subject || "(no subject)"}</div>
                {form.preview_text && <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{form.preview_text}</div>}
              </div>
              <div className="p-6 email-preview flex-1 overflow-auto min-h-[200px]" dangerouslySetInnerHTML={{ __html: activeVariant.content || '<p style="color:#A3A3A3">Start writing or use AI assist...</p>' }} />
            </div>
          </div>
        </div>

        <div className="space-y-6 border-t border-neutral-200 dark:border-neutral-800 pt-8">
          <div>
            <p className="label-overline mb-2">Step 02</p>
            <h2 className="font-display font-bold text-2xl tracking-tight">Compose</h2>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Mode</Label>
            <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
              <SelectTrigger className="mt-2 rounded-sm max-w-xl" data-testid="campaign-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
              {form.mode === "single" && "One subject and one body for everyone."}
              {form.mode === "test" && `Send to ${form.test_split_percent}% as a test. You pick the winner; rest gets the winning variant.`}
              {form.mode === "rotate" && "Each contact gets a randomly chosen variant. No winner."}
              {form.mode === "segmented" && "Each contact gets the variant matching their tag. Untagged contacts get the first variant."}
            </p>
          </div>

          {form.mode === "test" && (
            <div className="max-w-xs">
              <Label className="text-xs font-bold uppercase tracking-wider">Test % of audience</Label>
              <Input type="number" min="10" max="80" value={form.test_split_percent} onChange={(e) => setForm({ ...form, test_split_percent: parseInt(e.target.value) || 20 })} className="mt-2 rounded-sm" data-testid="campaign-split-input" />
            </div>
          )}

          {isMulti && (
            <div className="flex flex-wrap gap-2">
              {form.variants.map((v, idx) => (
                <div key={idx} className="relative">
                  <button
                    onClick={() => setActiveVariantIdx(idx)}
                    className={`px-3 py-2 rounded-sm border text-sm font-bold transition-colors ${
                      activeVariantIdx === idx ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 dark:border-neutral-800 hover:border-neutral-400"
                    }`}
                    data-testid={`variant-tab-${v.label}`}
                  >
                    Variant {v.label}
                  </button>
                  {form.variants.length > 1 && (
                    <button
                      onClick={() => removeVariant(idx)}
                      className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-600 text-white text-xs flex items-center justify-center"
                      data-testid={`variant-remove-${v.label}`}
                    >×</button>
                  )}
                </div>
              ))}
              <button onClick={addVariant} className="px-3 py-2 rounded-sm border border-dashed border-neutral-300 text-sm font-bold text-neutral-600 dark:text-neutral-400 hover:border-neutral-900 hover:text-neutral-900 dark:hover:text-neutral-50" data-testid="variant-add-button">
                <Plus className="h-3 w-3 inline mr-1" /> Add variant
              </button>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="label-overline">{isMulti ? `Variant ${activeVariant.label}` : "Email"}</p>
              <button onClick={() => { setTplOpen(true); }} className="text-xs font-bold inline-flex items-center gap-1 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-50" data-testid="load-template-button">
                <FileText className="h-3 w-3" /> Templates
              </button>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Subject line</Label>
                <button onClick={() => { setAiMode("subject"); setAiOpen(true); setAiResults([]); setAiContent(""); setAiPrompt(""); }} className="text-xs font-bold inline-flex items-center gap-1" style={{ color: "var(--brand-primary)" }} data-testid="ai-subject-button">
                  <Sparkles className="h-3 w-3" /> AI assist
                </button>
              </div>
              <Input value={activeVariant.subject} onChange={(e) => updateVariant(activeVariantIdx, { subject: e.target.value })} className="rounded-sm max-w-3xl" placeholder="A subject that earns the open" data-testid={`variant-subject-${activeVariant.label}`} />
            </div>

            {form.mode === "segmented" && (
              <div className="max-w-md">
                <Label className="text-xs font-bold uppercase tracking-wider">Tag for this variant</Label>
                <Select value={activeVariant.tag || "__none__"} onValueChange={(v) => updateVariant(activeVariantIdx, { tag: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="mt-2 rounded-sm" data-testid={`variant-tag-${activeVariant.label}`}><SelectValue placeholder="Fallback (no tag)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Fallback (any contact)</SelectItem>
                    {tags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Contacts with this tag get this variant.</p>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Content</Label>
                <button onClick={() => { setAiMode("content"); setAiOpen(true); setAiResults([]); setAiContent(""); setAiPrompt(""); }} className="text-xs font-bold inline-flex items-center gap-1" style={{ color: "var(--brand-primary)" }} data-testid="ai-content-button">
                  <Wand2 className="h-3 w-3" /> AI write
                </button>
              </div>
              <RichTextEditor value={activeVariant.content} onChange={(html) => updateVariant(activeVariantIdx, { content: html })} placeholder="Write your email — add paragraphs of text, not only images (improves deliverability scores)" />
            </div>
          </div>
        </div>
      </div>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Sparkles className="h-4 w-4" style={{ color: "var(--brand-primary)" }} />
              {aiMode === "subject" ? `Generate subject for ${activeVariant.label}` : `Generate body for ${activeVariant.label}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">What's the email about?</Label>
              <Textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={3} className="mt-2 rounded-sm" placeholder="Spring sale on hiking gear" data-testid="ai-prompt-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Tone</Label>
              <Select value={aiTone} onValueChange={setAiTone}>
                <SelectTrigger className="mt-2 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={runAI} disabled={aiLoading} className="w-full rounded-sm btn-brand font-semibold" data-testid="ai-generate-button">
              {aiLoading ? "Generating..." : "Generate"}
            </Button>
            {aiResults.length > 0 && (
              <div className="space-y-2 pt-2">
                <p className="label-overline">Suggestions</p>
                {aiResults.map((s, i) => (
                  <button key={i} onClick={() => applyAI(s)} className="w-full text-left p-3 rounded-sm border border-neutral-200 dark:border-neutral-800 hover:border-neutral-900 text-sm" data-testid={`ai-suggestion-${i}`}>{s}</button>
                ))}
              </div>
            )}
            {aiContent && (
              <div className="space-y-2 pt-2">
                <p className="label-overline">Generated content</p>
                <div className="p-4 rounded-sm border border-neutral-200 dark:border-neutral-800 max-h-60 overflow-auto email-preview text-sm" dangerouslySetInnerHTML={{ __html: aiContent }} />
                <Button onClick={() => applyAI(aiContent)} className="w-full rounded-sm font-semibold" variant="outline" data-testid="ai-apply-content">Use this content</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Load template into variant {activeVariant.label}</DialogTitle></DialogHeader>
          {templates.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 py-6 text-center">No templates yet.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-auto py-2">
              {templates.map((t) => (
                <button key={t.id} onClick={() => applyTemplate(t)} className="w-full text-left p-4 rounded-sm border border-neutral-200 dark:border-neutral-800 hover:border-neutral-900" data-testid={`apply-template-${t.id}`}>
                  <div className="font-semibold">{t.name}</div>
                  {t.preview_text && <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{t.preview_text}</div>}
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
